import supabase from "../config/supabase";

/**
 * Supabase Storage – image uploads (no DB).
 *
 * Setup in Supabase Dashboard:
 * 1. Storage → New bucket → name: "DevTeam" (IMAGES_BUCKET) → Public: ON
 * 2. Storage → Policies → "New policy" → For "storage.objects":
 *    - INSERT: with check (bucket_id = 'DevTeam') for role anon + authenticated
 *    - SELECT: for public read (or same bucket_id) for anon + authenticated
 *    Or run in SQL Editor (Storage policies):
 *      create policy "Allow DevTeam uploads" on storage.objects for insert
 *      to anon, authenticated with check (bucket_id = 'DevTeam');
 *      create policy "Allow DevTeam read" on storage.objects for select
 *      to anon, authenticated using (bucket_id = 'DevTeam');
 */
export const IMAGES_BUCKET = "DevTeam";

/**
 * Upload a file to Supabase Storage and return the public URL.
 * Use when the bucket is public.
 *
 * @param {string} bucket - Bucket name (default: IMAGES_BUCKET)
 * @param {string} path - Path inside the bucket (e.g. "avatars/userId/photo.jpg")
 * @param {File} file - The file to upload
 * @param {{ upsert?: boolean, contentType?: string }} options - Optional upsert (overwrite) and contentType
 * @returns {{ publicUrl: string | null, error: Error | null }}
 */
export async function uploadImage(bucket, path, file, options = {}) {
  if (!supabase) {
    return {
      publicUrl: null,
      error: new Error(
        "Supabase is not configured. Check .env for SUPABASE_URL and SUPABASE_ANON_KEY.",
      ),
    };
  }

  const { upsert = true, contentType = file?.type || "image/jpeg" } = options;

  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { upsert, contentType });

    if (error) {
      const isBucketNotFound =
        error?.message?.toLowerCase().includes("bucket") &&
        (error?.message?.toLowerCase().includes("not found") ||
          error?.message?.toLowerCase().includes("does not exist"));
      const helpfulError = isBucketNotFound
        ? new Error(
            `Bucket "${bucket}" not found. Create it in Supabase: Dashboard → Storage → New bucket → name "${bucket}" → set Public → Save.`,
          )
        : error;
      return { publicUrl: null, error: helpfulError };
    }

    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(data.path);
    return { publicUrl: urlData.publicUrl, error: null };
  } catch (err) {
    return { publicUrl: null, error: err };
  }
}

/**
 * Get the public URL for a file in a bucket (no upload).
 */
export function getPublicUrl(bucket, path) {
  if (!supabase) return null;
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Delete a file from a bucket.
 */
export async function deleteFile(bucket, path) {
  if (!supabase) {
    return { error: new Error("Supabase is not configured.") };
  }
  const { error } = await supabase.storage.from(bucket).remove([path]);
  return { error };
}

/**
 * Upload a general file (document, PDF, etc.) to Supabase Storage and return the public URL.
 * Similar to uploadImage but accepts any file type.
 *
 * @param {string} bucket - Bucket name (default: IMAGES_BUCKET)
 * @param {string} path - Path inside the bucket (e.g. "documents/taskId/file.pdf")
 * @param {File} file - The file to upload
 * @param {{ upsert?: boolean }} options - Optional upsert (overwrite)
 * @returns {{ publicUrl: string | null, error: Error | null, fileName: string }}
 */
export async function uploadFile(bucket, path, file, options = {}) {
  if (!supabase) {
    return {
      publicUrl: null,
      fileName: file?.name || "unknown",
      error: new Error(
        "Supabase is not configured. Check .env for SUPABASE_URL and SUPABASE_ANON_KEY.",
      ),
    };
  }

  const { upsert = true } = options;
  const contentType = file?.type || "application/octet-stream";

  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { upsert, contentType });

    if (error) {
      const isBucketNotFound =
        error?.message?.toLowerCase().includes("bucket") &&
        (error?.message?.toLowerCase().includes("not found") ||
          error?.message?.toLowerCase().includes("does not exist"));
      const helpfulError = isBucketNotFound
        ? new Error(
            `Bucket "${bucket}" not found. Create it in Supabase: Dashboard → Storage → New bucket → name "${bucket}" → set Public → Save.`,
          )
        : error;
      return {
        publicUrl: null,
        fileName: file?.name || "unknown",
        error: helpfulError,
      };
    }

    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(data.path);
    return {
      publicUrl: urlData.publicUrl,
      fileName: file?.name || "unknown",
      error: null,
    };
  } catch (err) {
    return { publicUrl: null, fileName: file?.name || "unknown", error: err };
  }
}

/**
 * Check if Supabase Storage is available (client configured).
 */
export function isSupabaseStorageAvailable() {
  return !!supabase;
}
